import React from 'react';
import { API } from '../api';
import { downloadFile } from '../utils/download';

/*
 * A member's record: who changed what, and a copy of everything held.
 *
 * Two things the club now owes a member, side by side on the profile —
 * for admins only:
 *   - an answer to "who marked me unfinancial?" — the change history;
 *   - a copy of their personal information, which the Privacy Act gives
 *     everyone the right to. The member asks the club; the club checks it
 *     is really them and downloads it here. Not a button on the member's
 *     own profile: a file of personal details goes out through a person.
 */

const whenText = (v) => {
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

async function download(userId, showToast) {
  try {
    await downloadFile(`${API}/api/users/${userId}/export`, 'greensheet-information.json');
  } catch (err) {
    if (showToast) showToast(err.message, 'red');
  }
}

function MemberRecordCard({ userId, showToast }) {
  const [history, setHistory] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  /* Loaded when opened, not with the profile: most visits to a profile are
     not to read its history. */
  React.useEffect(() => {
    if (!open || history) return;
    fetch(`${API}/api/users/${userId}/history`)
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('Could not load the history'))))
      .then(setHistory)
      .catch(err => setError(err.message));
  }, [open, history, userId]);

  React.useEffect(() => { setHistory(null); setOpen(false); setError(''); }, [userId]);

  const onDownload = async () => {
    setBusy(true);
    await download(userId, showToast);
    setBusy(false);
  };

  return (
    <div className="w3-card w3-round w3-white w3-margin-top">
      <div className="w3-container w3-theme-l4 w3-round-top" style={{ padding: '12px 16px' }}>
        <strong><i className="fa fa-folder-open-o w3-margin-right"></i>Record</strong>
      </div>
      <div className="w3-container w3-padding">
        <p className="w3-small w3-text-grey" style={{ marginTop: 0 }}>
          Download everything GreenSheet holds about this member — for when they ask for a copy
          of their information. Check it is really them asking first.{' '}
          <a href="/privacy.html#rights" target="_blank" rel="noopener">Their privacy rights</a>
        </p>
        <button type="button" className="w3-button w3-round w3-theme-l4" onClick={onDownload} disabled={busy}>
          <i className={`fa ${busy ? 'fa-spinner fa-spin' : 'fa-download'} w3-margin-right`} aria-hidden="true"></i>
          Download their information
        </button>

        <details className="member-history" open={open} onToggle={e => setOpen(e.currentTarget.open)}>
            <summary>Change history</summary>
            {error && <p className="w3-text-red w3-small">{error}</p>}
            {open && !history && !error && <p className="w3-small w3-text-grey">Loading…</p>}
            {history && history.length === 0 && (
              <p className="w3-small w3-text-grey">No changes recorded yet. Changes are recorded from now on.</p>
            )}
            {history && history.length > 0 && (
              <ol className="member-history-list">
                {history.map(h => (
                  <li key={h.id}>
                    <div className="member-history-head">
                      <strong>{h.action}</strong>
                      <span>{h.by} · {whenText(h.when)}</span>
                    </div>
                    {h.changes.length > 0 && (
                      <ul>
                        {h.changes.map((c, i) => (
                          <li key={i}>
                            {c.label}: {c.from === null ? c.to : <>{c.from} <span aria-hidden="true">→</span><span className="sr-only"> to </span> {c.to}</>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ol>
            )}
        </details>
      </div>
    </div>
  );
}

export default MemberRecordCard;
